import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import { Plus, Upload, Save, Tv, Send } from 'lucide-react';
import { api, apiUrl } from '../api';

const MARKER = 44;

export default function FloorPlan({ socket, userId }) {
  const [plans, setPlans] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [placements, setPlacements] = useState([]); // [{device_uuid, x_pct, y_pct}]
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [error, setError] = useState('');
  const fileInput = useRef(null);
  const stageRef = useRef(null);

  const active = plans.find((p) => p.id === activeId) || null;

  const load = useCallback(async () => {
    try {
      const [fp, d, t] = [await api.listFloorplans(userId), await api.listDevices(userId), await api.listTemplates(userId)];
      setPlans(fp.floorplans);
      setDevices(d.devices);
      setTemplates(t.templates);
      if (!activeId && fp.floorplans[0]) setActiveId(fp.floorplans[0].id);
      setError('');
    } catch (e) { setError(e.message); }
  }, [userId, activeId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [userId]);
  useEffect(() => { if (active) setPlacements(active.devices || []); }, [active]);

  const createPlan = async () => {
    const name = window.prompt('Floor plan name?', 'Ground floor');
    if (!name) return;
    const { floorplan } = await api.createFloorplan(name, userId);
    await load();
    setActiveId(floorplan.id);
  };

  const onUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file || !active) return;
    try { await api.uploadFloorplanImage(active.id, file); await load(); }
    catch (err) { setError(err.message); }
    finally { e.target.value = ''; }
  };

  const addDeviceToPlan = (uuid) => {
    if (placements.some((p) => p.device_uuid === uuid)) return;
    setPlacements((prev) => [...prev, { device_uuid: uuid, x_pct: 50, y_pct: 50 }]);
  };

  const moveMarker = (uuid, xPct, yPct) =>
    setPlacements((prev) => prev.map((p) => (p.device_uuid === uuid ? { ...p, x_pct: xPct, y_pct: yPct } : p)));

  const savePlacements = async () => {
    await api.saveFloorplanDevices(active.id, placements);
    await load();
  };

  const pushToDevice = (uuid, templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl || !socket) return;
    socket.emit('display_command', { command: 'show_template', params: { id: tpl.id, name: tpl.name, ...tpl.json }, userId, deviceId: uuid });
  };

  const deviceInfo = (uuid) => devices.find((d) => d.deviceUUID === uuid);
  const unplaced = devices.filter((d) => !placements.some((p) => p.device_uuid === d.deviceUUID));
  const rect = () => stageRef.current?.getBoundingClientRect() || { width: 1, height: 1 };

  return (
    <div>
      <div className="page-head">
        <div className="page-title">
          <h2>Floor plan</h2>
          <p>Position your screens on a map of the space.</p>
        </div>
        <div className="page-actions">
          <select className="select" style={{ width: 'auto' }} value={activeId || ''}
            onChange={(e) => setActiveId(e.target.value)}>
            <option value="">Select floor plan…</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="btn btn-ghost" onClick={createPlan}><Plus size={16} /> New plan</button>
          {active && (
            <>
              <button className="btn btn-ghost" onClick={() => fileInput.current.click()}>
                <Upload size={16} /> Upload image
              </button>
              <input ref={fileInput} type="file" accept="image/*" onChange={onUploadImage} style={{ display: 'none' }} />
              {unplaced.length > 0 && (
                <select className="select" style={{ width: 'auto' }} value=""
                  onChange={(e) => e.target.value && addDeviceToPlan(e.target.value)}>
                  <option value="">+ Place a TV…</option>
                  {unplaced.map((d) => <option key={d.deviceUUID} value={d.deviceUUID}>{d.name || d.deviceUUID.slice(0, 8)}</option>)}
                </select>
              )}
              <button className="btn btn-primary" onClick={savePlacements}><Save size={16} /> Save layout</button>
            </>
          )}
        </div>
      </div>

      {error && <p className="error-text">{error}</p>}
      {!active && <div className="empty">Create a floor plan and upload an image to position your TVs.</div>}

      {active && (
        <div ref={stageRef} className="plan-stage"
          style={active.image_path ? { backgroundImage: `url(${apiUrl(active.image_path)})` } : undefined}>
          {!active.image_path && <div className="stage-hint">Upload a floor-plan image</div>}
          {placements.map((pl) => {
            const r = rect();
            const x = (pl.x_pct / 100) * r.width - MARKER / 2;
            const y = (pl.y_pct / 100) * r.height - MARKER / 2;
            const info = deviceInfo(pl.device_uuid);
            const online = info?.connected;
            return (
              <Rnd key={pl.device_uuid} bounds="parent" size={{ width: MARKER, height: MARKER }} position={{ x, y }}
                enableResizing={false}
                onDragStop={(e, d) => {
                  const rr = rect();
                  moveMarker(pl.device_uuid, ((d.x + MARKER / 2) / rr.width) * 100, ((d.y + MARKER / 2) / rr.height) * 100);
                }}
                onClick={() => setSelectedDevice(pl.device_uuid)}>
                <div title={info?.name || pl.device_uuid}
                  className={`marker${online ? ' online' : ''}${selectedDevice === pl.device_uuid ? ' selected' : ''}`}>
                  <Tv size={20} />
                </div>
              </Rnd>
            );
          })}
        </div>
      )}

      {selectedDevice && (
        <div className="card card-pad" style={{ marginTop: 16, maxWidth: 1100 }}>
          <div style={{ marginBottom: 12, fontWeight: 600 }}>
            {deviceInfo(selectedDevice)?.name || selectedDevice.slice(0, 8)} — push content
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {templates.map((t) => (
              <button key={t.id} className="btn btn-ghost btn-sm" onClick={() => pushToDevice(selectedDevice, t.id)}>
                <Send size={16} /> {t.name}
              </button>
            ))}
            {templates.length === 0 && <span style={{ color: 'var(--muted)', fontSize: 13 }}>No templates yet.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
